// ============================================
// SUPABASE DATA LAYER - ALL DATA PERSISTED TO SUPABASE
// Multi-event scoped: every event-specific query is filtered by the active event_id.
// ============================================

import { supabase } from './supabase';
import type {
  Patient,
  Vital,
  User,
  ActivityLog,
  EditRequest,
  PatientEditRequest,
  WhatsAppShareRequest,
  Bank,
  FeeCollection,
  Expense,
  CashHolding,
  CashTransfer,
  BankTransfer,
  MedicalEvent,
  EventStatus,
} from '../types';
import { getDeviceInfo, getIPAddress } from './device';
import { getActiveEventId } from './event';

const eventId = (): string => getActiveEventId();

const EVENT_STATUS_ORDER: Record<EventStatus, number> = {
  active: 0,
  completed: 1,
  draft: 2,
  archived: 3,
};

// ============================================
// EVENTS
// ============================================

/** Map a raw events row to a MedicalEvent. */
const mapEvent = (e: Record<string, unknown>): MedicalEvent => ({
  id: e.id as string,
  eventName: e.event_name as string,
  eventCode: e.event_code as string,
  eventDate: e.event_date as string,
  startTime: e.start_time as string | undefined,
  endTime: e.end_time as string | undefined,
  venue: e.venue as string | undefined,
  location: e.location as string | undefined,
  description: e.description as string | undefined,
  organizerName: e.organizer_name as string | undefined,
  contactPerson: e.contact_person as string | undefined,
  contactNumber: e.contact_number as string | undefined,
  email: e.email as string | undefined,
  fee: e.fee === null || e.fee === undefined ? 0 : Number(e.fee),
  status: (e.status as EventStatus) || 'active',
  createdAt: e.created_at as string,
  updatedAt: e.updated_at as string,
});

// Gentle backoff schedule (ms) so brief network blips are ridden out rather
// than surfacing as a scary error screen.
const EVENT_RETRY_DELAYS = [400, 800, 1500, 2800, 4000];

export const getEvents = async (throwOnError = false): Promise<MedicalEvent[]> => {
  const MAX_RETRIES = EVENT_RETRY_DELAYS.length;
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        const isNetworkError = /Failed to fetch|NetworkError|fetch failed|Load failed/i.test(error.message || '');
        if (attempt < MAX_RETRIES && isNetworkError) {
          lastError = error;
          await new Promise((res) => setTimeout(res, EVENT_RETRY_DELAYS[attempt - 1]));
          continue;
        }
        console.error('Error fetching events:', error);
        if (throwOnError) throw error;
        return [];
      }

      return (data || [])
        .map((e: Record<string, unknown>) => mapEvent(e))
        .sort(
          (a, b) =>
            EVENT_STATUS_ORDER[a.status] - EVENT_STATUS_ORDER[b.status] ||
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
    } catch (err) {
      lastError = err;
      if (attempt < MAX_RETRIES) {
        await new Promise((res) => setTimeout(res, EVENT_RETRY_DELAYS[attempt - 1]));
        continue;
      }
    }
  }

  console.error('Error fetching events (after retries):', lastError);
  if (throwOnError) throw lastError instanceof Error ? lastError : new Error('Failed to fetch events');
  return [];
};

export const getEventById = async (id: string): Promise<MedicalEvent | null> => {
  const { data, error } = await supabase
    .from('events')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error || !data) {
    console.error('Error fetching event:', error);
    return null;
  }

  return mapEvent(data as Record<string, unknown>);
};

export const addEvent = async (event: MedicalEvent): Promise<void> => {
  const { error } = await supabase
    .from('events')
    .insert([{
      id: event.id,
      event_name: event.eventName,
      event_code: event.eventCode,
      event_date: event.eventDate,
      start_time: event.startTime ?? null,
      end_time: event.endTime ?? null,
      venue: event.venue ?? null,
      location: event.location ?? null,
      description: event.description ?? null,
      organizer_name: event.organizerName ?? null,
      contact_person: event.contactPerson ?? null,
      contact_number: event.contactNumber ?? null,
      email: event.email ?? null,
      fee: event.fee ?? 0,
      status: event.status,
      created_at: event.createdAt,
      updated_at: event.updatedAt,
    }]);

  if (error) {
    console.error('Error adding event:', error);
    throw error;
  }
};

export const updateEvent = async (id: string, updates: Partial<MedicalEvent>): Promise<void> => {
  const dbUpdates: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (updates.eventName !== undefined) dbUpdates.event_name = updates.eventName;
  if (updates.eventCode !== undefined) dbUpdates.event_code = updates.eventCode;
  if (updates.eventDate !== undefined) dbUpdates.event_date = updates.eventDate;
  if (updates.startTime !== undefined) dbUpdates.start_time = updates.startTime;
  if (updates.endTime !== undefined) dbUpdates.end_time = updates.endTime;
  if (updates.venue !== undefined) dbUpdates.venue = updates.venue;
  if (updates.location !== undefined) dbUpdates.location = updates.location;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.organizerName !== undefined) dbUpdates.organizer_name = updates.organizerName;
  if (updates.contactPerson !== undefined) dbUpdates.contact_person = updates.contactPerson;
  if (updates.contactNumber !== undefined) dbUpdates.contact_number = updates.contactNumber;
  if (updates.email !== undefined) dbUpdates.email = updates.email;
  if (updates.fee !== undefined) dbUpdates.fee = updates.fee;
  if (updates.status !== undefined) dbUpdates.status = updates.status;

  const { error } = await supabase
    .from('events')
    .update(dbUpdates)
    .eq('id', id);

  if (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

export const updateEventStatus = async (id: string, status: EventStatus): Promise<void> => {
  await updateEvent(id, { status });
};

/** Per-event record counts used by the Events management page. */
export const getEventPatientCount = async (id: string): Promise<number> => {
  const { count, error } = await supabase
    .from('patients')
    .select('id', { count: 'exact', head: true })
    .eq('event_id', id);
  if (error) { console.error('Error counting patients:', error); return 0; }
  return count || 0;
};

export const getEventUserCount = async (id: string): Promise<number> => {
  const { count, error } = await supabase
    .from('user_events')
    .select('user_id', { count: 'exact', head: true })
    .eq('event_id', id);
  if (error) { console.error('Error counting users:', error); return 0; }
  return count || 0;
};

// ============================================
// USER <-> EVENT ASSIGNMENT
// ============================================

/** List app users assigned to a given event. */
export const getEventUsers = async (id: string): Promise<User[]> => {
  const { data, error } = await supabase
    .from('user_events')
    .select('user_id')
    .eq('event_id', id);

  if (error || !data) {
    console.error('Error fetching event users:', error);
    return [];
  }

  const userIds = (data as { user_id: string }[]).map((r) => r.user_id);
  if (userIds.length === 0) return [];

  const { data: users, error: userError } = await supabase
    .from('app_users')
    .select('id, username, password, display_name, role, is_active, is_online, logout_timer, created_at, created_by, last_login_at')
    .in('id', userIds)
    .order('username', { ascending: true });

  if (userError || !users) {
    console.error('Error fetching users for event:', userError);
    return [];
  }

  return (users as Record<string, unknown>[]).map((u) => ({
    id: u.id as string,
    username: u.username as string,
    password: u.password as string,
    displayName: u.display_name as string | undefined,
    role: u.role as User['role'],
    isActive: u.is_active as boolean,
    isOnline: u.is_online as boolean,
    logoutTimer: u.logout_timer as number,
    createdAt: u.created_at as string,
    createdBy: u.created_by as string,
    lastLoginAt: u.last_login_at as string | undefined,
    eventIds: [id],
  }));
};

/** Return the set of event ids assigned to a user. */
export const getUserEventIds = async (userId: string): Promise<string[]> => {
  const { data, error } = await supabase
    .from('user_events')
    .select('event_id')
    .eq('user_id', userId);

  if (error) {
    console.error('Error fetching user event ids:', error);
    return [];
  }
  return (data || []).map((r: Record<string, unknown>) => r.event_id as string);
};

export const assignUserToEvent = async (userId: string, eventId: string): Promise<void> => {
  // idempotent-ish: check existing
  const { data, error } = await supabase
    .from('user_events')
    .select('user_id')
    .eq('user_id', userId)
    .eq('event_id', eventId)
    .maybeSingle();

  if (error) {
    console.error('Error checking user event assignment:', error);
    return;
  }
  if (data) return;

  const { error: insertError } = await supabase
    .from('user_events')
    .insert([{
      user_id: userId,
      event_id: eventId,
      created_at: new Date().toISOString(),
    }]);

  if (insertError) {
    console.error('Error assigning user to event:', insertError);
    throw insertError;
  }
};

export const unassignUserFromEvent = async (userId: string, eventId: string): Promise<void> => {
  const { error } = await supabase
    .from('user_events')
    .delete()
    .eq('user_id', userId)
    .eq('event_id', eventId);

  if (error) {
    console.error('Error unassigning user from event:', error);
    throw error;
  }
};

// ============================================
// PATIENTS
// ============================================

export const getPatients = async (): Promise<Patient[]> => {
  const { data, error } = await supabase
    .from('patients')
    .select('*')
    .eq('event_id', eventId())
    .order('registered_at', { ascending: true });

  if (error) {
    console.error('Error fetching patients:', error);
    return [];
  }

  return (data || []).map((p: Record<string, unknown>) => ({
    id: p.id as string,
    name: p.name as string,
    gender: p.gender as 'Male' | 'Female' | 'Other',
    dateOfBirth: p.date_of_birth as string,
    age: p.age as number,
    bloodGroup: p.blood_group as string,
    email: p.email as string | undefined,
    contactNumber: p.contact_number as string,
    whatsappNumber: p.whatsapp_number as string | undefined,
    address: p.address as string | undefined,
    college: p.college as string,
    department: p.department as string,
    yearDesignation: p.year_designation as string,
    registeredAt: p.registered_at as string,
    registeredBy: p.registered_by as string,
    paymentStatus: p.payment_status as 'paid' | 'unpaid' | undefined,
    patientType: p.patient_type as 'student' | 'faculty' | 'other' | undefined,
    studentYear: p.student_year as string | undefined,
    designation: p.designation as string | undefined,
    whatsappShared: p.whatsapp_shared as boolean | undefined,
    whatsappSharedAt: p.whatsapp_shared_at as string | undefined,
  }));
};

/**
 * Fetch ALL patient IDs across every event (not scoped to the active event).
 * The patient `id` is the global primary key, so sequential IDs must be unique
 * across the entire table, not just within a single event.
 */
export const getAllPatientIds = async (): Promise<string[]> => {
  const { data, error } = await supabase
    .from('patients')
    .select('id');

  if (error) {
    console.error('Error fetching all patient ids:', error);
    return [];
  }

  return (data || []).map((p: Record<string, unknown>) => p.id as string);
};

export const addPatient = async (patient: Patient): Promise<void> => {
  const { error } = await supabase
    .from('patients')
    .insert([{
      id: patient.id,
      event_id: eventId(),
      name: patient.name,
      gender: patient.gender,
      date_of_birth: patient.dateOfBirth,
      age: patient.age,
      blood_group: patient.bloodGroup,
      email: patient.email ?? null,
      contact_number: patient.contactNumber,
      whatsapp_number: patient.whatsappNumber ?? null,
      address: patient.address ?? null,
      college: patient.college,
      department: patient.department,
      year_designation: patient.yearDesignation,
      registered_at: patient.registeredAt,
      registered_by: patient.registeredBy,
      payment_status: patient.paymentStatus ?? 'unpaid',
      patient_type: patient.patientType ?? 'student',
      student_year: patient.studentYear ?? null,
      designation: patient.designation ?? null,
      whatsapp_shared: patient.whatsappShared ?? false,
      whatsapp_shared_at: patient.whatsappSharedAt ?? null,
    }]);

  if (error) {
    console.error('Error adding patient:', error);
    throw error;
  }
};

export const updatePatient = async (id: string, updates: Partial<Patient>): Promise<void> => {
  const dbUpdates: Record<string, unknown> = {};
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.gender !== undefined) dbUpdates.gender = updates.gender;
  if (updates.dateOfBirth !== undefined) dbUpdates.date_of_birth = updates.dateOfBirth;
  if (updates.age !== undefined) dbUpdates.age = updates.age;
  if (updates.bloodGroup !== undefined) dbUpdates.blood_group = updates.bloodGroup;
  if (updates.email !== undefined) dbUpdates.email = updates.email;
  if (updates.contactNumber !== undefined) dbUpdates.contact_number = updates.contactNumber;
  if (updates.whatsappNumber !== undefined) dbUpdates.whatsapp_number = updates.whatsappNumber;
  if (updates.address !== undefined) dbUpdates.address = updates.address;
  if (updates.college !== undefined) dbUpdates.college = updates.college;
  if (updates.department !== undefined) dbUpdates.department = updates.department;
  if (updates.yearDesignation !== undefined) dbUpdates.year_designation = updates.yearDesignation;
  if (updates.registeredAt !== undefined) dbUpdates.registered_at = updates.registeredAt;
  if (updates.registeredBy !== undefined) dbUpdates.registered_by = updates.registeredBy;
  if (updates.paymentStatus !== undefined) dbUpdates.payment_status = updates.paymentStatus;
  if (updates.patientType !== undefined) dbUpdates.patient_type = updates.patientType;
  if (updates.studentYear !== undefined) dbUpdates.student_year = updates.studentYear;
  if (updates.designation !== undefined) dbUpdates.designation = updates.designation;
  if (updates.whatsappShared !== undefined) dbUpdates.whatsapp_shared = updates.whatsappShared;
  if (updates.whatsappSharedAt !== undefined) dbUpdates.whatsapp_shared_at = updates.whatsappSharedAt;

  const { error } = await supabase
    .from('patients')
    .update(dbUpdates)
    .eq('id', id)
    .eq('event_id', eventId());

  if (error) {
    console.error('Error updating patient:', error);
    throw error;
  }
};

export const deletePatient = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('patients')
    .delete()
    .eq('id', id)
    .eq('event_id', eventId());

  if (error) {
    console.error('Error deleting patient:', error);
    throw error;
  }
};

// ============================================
// VITALS
// ============================================

export const getVitals = async (): Promise<Vital[]> => {
  const { data, error } = await supabase
    .from('vitals')
    .select('*')
    .eq('event_id', eventId())
    .order('recorded_at', { ascending: false });

  if (error) {
    console.error('Error fetching vitals:', error);
    return [];
  }

  return (data || []).map((v: Record<string, unknown>) => ({
    id: v.id as string,
    patientId: v.patient_id as string,
    type: v.type as string,
    value: v.value as string,
    unit: v.unit as string | undefined,
    recordedAt: v.recorded_at as string,
    recordedBy: v.recorded_by as string,
    editRequested: v.edit_requested as boolean | undefined,
    editRequestId: v.edit_request_id as string | undefined,
    edited: v.edited as boolean | undefined,
    previousValue: v.previous_value as string | undefined,
    editedAt: v.edited_at as string | undefined,
    editedBy: v.edited_by as string | undefined,
  }));
};

export const getPatientVitals = async (patientId: string): Promise<Vital[]> => {
  const { data, error } = await supabase
    .from('vitals')
    .select('*')
    .eq('patient_id', patientId)
    .eq('event_id', eventId())
    .order('recorded_at', { ascending: false });

  if (error) {
    console.error('Error fetching patient vitals:', error);
    return [];
  }

  return (data || []).map((v: Record<string, unknown>) => ({
    id: v.id as string,
    patientId: v.patient_id as string,
    type: v.type as string,
    value: v.value as string,
    unit: v.unit as string | undefined,
    recordedAt: v.recorded_at as string,
    recordedBy: v.recorded_by as string,
    editRequested: v.edit_requested as boolean | undefined,
    editRequestId: v.edit_request_id as string | undefined,
    edited: v.edited as boolean | undefined,
    previousValue: v.previous_value as string | undefined,
    editedAt: v.edited_at as string | undefined,
    editedBy: v.edited_by as string | undefined,
  }));
};

export const addVital = async (vital: Vital): Promise<void> => {
  const { error } = await supabase
    .from('vitals')
    .insert([{
      id: vital.id,
      event_id: eventId(),
      patient_id: vital.patientId,
      type: vital.type,
      value: vital.value,
      unit: vital.unit ?? null,
      recorded_at: vital.recordedAt,
      recorded_by: vital.recordedBy,
      edit_requested: vital.editRequested ?? false,
      edit_request_id: vital.editRequestId ?? null,
      edited: vital.edited ?? false,
      previous_value: vital.previousValue ?? null,
      edited_at: vital.editedAt ?? null,
      edited_by: vital.editedBy ?? null,
    }]);

  if (error) {
    console.error('Error adding vital:', error);
    throw error;
  }
};

export const updateVital = async (id: string, updates: Partial<Vital>): Promise<void> => {
  const dbUpdates: Record<string, unknown> = {};
  if (updates.value !== undefined) dbUpdates.value = updates.value;
  if (updates.unit !== undefined) dbUpdates.unit = updates.unit;
  if (updates.editRequested !== undefined) dbUpdates.edit_requested = updates.editRequested;
  if (updates.editRequestId !== undefined) dbUpdates.edit_request_id = updates.editRequestId;
  if (updates.edited !== undefined) dbUpdates.edited = updates.edited;
  if (updates.previousValue !== undefined) dbUpdates.previous_value = updates.previousValue;
  if (updates.editedAt !== undefined) dbUpdates.edited_at = updates.editedAt;
  if (updates.editedBy !== undefined) dbUpdates.edited_by = updates.editedBy;

  const { error } = await supabase
    .from('vitals')
    .update(dbUpdates)
    .eq('id', id)
    .eq('event_id', eventId());

  if (error) {
    console.error('Error updating vital:', error);
    throw error;
  }
};

// ============================================
// USERS
// ============================================

export const getUsers = async (): Promise<User[]> => {
  const { data, error } = await supabase
    .from('app_users')
    .select('id, username, password, display_name, role, is_active, is_online, logout_timer, created_at, created_by, last_login_at')
    .order('username', { ascending: true });

  if (error) {
    console.error('Error fetching users:', error);
    return [];
  }

  return (data || []).map((u: Record<string, unknown>) => ({
    id: u.id as string,
    username: u.username as string,
    password: u.password as string,
    displayName: u.display_name as string | undefined,
    role: u.role as User['role'],
    isActive: u.is_active as boolean,
    isOnline: u.is_online as boolean,
    logoutTimer: u.logout_timer as number,
    createdAt: u.created_at as string,
    createdBy: u.created_by as string,
    lastLoginAt: u.last_login_at as string | undefined,
  }));
};

export const addUser = async (user: User): Promise<void> => {
  const { error } = await supabase
    .from('app_users')
    .insert([{
      id: user.id,
      username: user.username,
      password: user.password,
      display_name: user.displayName,
      role: user.role,
      is_active: user.isActive ?? true,
      is_online: user.isOnline ?? false,
      logout_timer: user.logoutTimer ?? 5,
      created_at: user.createdAt,
      created_by: user.createdBy,
      last_login_at: user.lastLoginAt,
    }]);

  if (error) {
    console.error('Error adding user:', error);
    throw error;
  }
};

export const updateUser = async (id: string, updates: Partial<User>): Promise<void> => {
  const dbUpdates: Record<string, unknown> = {};
  if (updates.password !== undefined) dbUpdates.password = updates.password;
  if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName;
  if (updates.role !== undefined) dbUpdates.role = updates.role;
  if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
  if (updates.isOnline !== undefined) dbUpdates.is_online = updates.isOnline;
  if (updates.logoutTimer !== undefined) dbUpdates.logout_timer = updates.logoutTimer;
  if (updates.lastLoginAt !== undefined) dbUpdates.last_login_at = updates.lastLoginAt;
  if ((updates as Record<string, unknown>).status !== undefined) {
    dbUpdates.is_active = (updates as Record<string, unknown>).status === 'active';
  }

  const { error } = await supabase
    .from('app_users')
    .update(dbUpdates)
    .eq('id', id);

  if (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const deleteUser = async (id: string): Promise<void> => {
  // Remove event assignments first
  const { error: ueError } = await supabase
    .from('user_events')
    .delete()
    .eq('user_id', id);
  if (ueError) console.warn('Error clearing user events:', ueError);

  const { error } = await supabase
    .from('app_users')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export const updateUserRole = async (userId: string, role: User['role']): Promise<void> => {
  await updateUser(userId, { role });
};

export const updateUserPassword = async (userId: string, password: string): Promise<void> => {
  await updateUser(userId, { password });
};

export const updateUserStatus = async (userId: string, status: 'active' | 'inactive'): Promise<void> => {
  const { error } = await supabase
    .from('app_users')
    .update({ is_active: status === 'active' })
    .eq('id', userId);

  if (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};

export const updateUserLoginStatus = async (userId: string, isOnline: boolean): Promise<void> => {
  const dbUpdates: Record<string, unknown> = { is_online: isOnline };
  if (isOnline) {
    dbUpdates.last_login_at = new Date().toISOString();
  }

  const { error } = await supabase
    .from('app_users')
    .update(dbUpdates)
    .eq('id', userId);

  if (error) {
    console.error('Error updating user login status:', error);
  }
};

// ============================================
// ACTIVITY LOGS
// ============================================

const getNowUTC = (): string => {
  return new Date().toISOString();
};

const mapLog = (l: Record<string, unknown>): ActivityLog => ({
  id: l.id as string,
  userId: l.user_id as string,
  username: l.username as string,
  action: l.action as string,
  details: l.details as string,
  ipAddress: l.ip_address as string,
  deviceName: l.device_name as string,
  browser: l.browser as string,
  deviceManufacturer: l.device_manufacturer as string | undefined,
  deviceModel: l.device_model as string | undefined,
  timestamp: l.timestamp as string,
  eventId: l.event_id as string | undefined,
  role: l.role as string | undefined,
  module: l.module as string | undefined,
  testId: l.test_id as string | undefined,
  patientId: l.patient_id as string | undefined,
  description: l.description as string | undefined,
  metadata: (l.metadata as Record<string, unknown>) || undefined,
});

/**
 * Server-side filtered activity log query (event-scoped, or all events).
 */
export interface LogFilters {
  scope: string; // eventId or 'all'
  username?: string;
  role?: string;
  module?: string;
  testId?: string;
  action?: string;
  patientId?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export const queryLogs = async (filters: LogFilters): Promise<ActivityLog[]> => {
  let q = supabase.from('activity_logs').select('*');
  if (filters.scope && filters.scope !== 'all') q = q.eq('event_id', filters.scope);
  if (filters.username) q = q.eq('username', filters.username);
  if (filters.role) q = q.eq('role', filters.role);
  if (filters.module) q = q.eq('module', filters.module);
  if (filters.testId) q = q.eq('test_id', filters.testId);
  if (filters.action) q = q.eq('action', filters.action);
  if (filters.patientId) q = q.eq('patient_id', filters.patientId);
  if (filters.from) q = q.gte('timestamp', new Date(filters.from).toISOString());
  if (filters.to) q = q.lte('timestamp', new Date(filters.to).toISOString());
  const { data, error } = await q.order('timestamp', { ascending: false }).limit(filters.limit || 2000);

  if (error) {
    console.error('Error querying logs:', error);
    return [];
  }
  return (data || []).map((l: Record<string, unknown>) => mapLog(l));
};

/**
 * Server-side event-scoped activity log fetch.
 * Pass 'all' to retrieve logs across every event (master admin ALL ACTIVITY).
 */
export const getLogsForEvent = async (scope: string): Promise<ActivityLog[]> => {
  let query = supabase.from('activity_logs').select('*');
  if (scope !== 'all' && scope) query = query.eq('event_id', scope);
  const { data, error } = await query.order('timestamp', { ascending: false }).limit(2000);

  if (error) {
    console.error('Error fetching logs for event:', error);
    return [];
  }
  return (data || []).map((l: Record<string, unknown>) => mapLog(l));
};

/** Event-scoped logs for the active event (backward-compatible wrapper). */
export const getLogs = async (): Promise<ActivityLog[]> => {
  return getLogsForEvent(eventId());
};

export const addLog = async (log: ActivityLog): Promise<void> => {
  try {
    if (!log.userId || !log.username || !log.action || !log.details || !log.timestamp) {
      console.error('Activity log missing required fields');
      return;
    }

    const { error } = await supabase
      .from('activity_logs')
      .insert([{
        event_id: log.eventId || eventId(),
        user_id: log.userId,
        username: log.username,
        role: log.role ?? null,
        action: log.action,
        module: log.module ?? null,
        test_id: log.testId ?? null,
        patient_id: log.patientId ?? null,
        details: log.details,
        description: log.description ?? null,
        ip_address: log.ipAddress || 'unknown',
        device_name: log.deviceName || 'unknown',
        browser: log.browser || 'unknown',
        device_manufacturer: log.deviceManufacturer ?? null,
        device_model: log.deviceModel ?? null,
        metadata: log.metadata ?? null,
        timestamp: log.timestamp,
      }]);

    if (error) {
      console.error('Error inserting activity log:', error.message);
    }
  } catch (err) {
    console.error('Unexpected error in addLog:', err);
  }
};

export const createLogEntry = async (
  user: { id: string; username: string; role?: string } | null,
  action: string,
  details: string
): Promise<void> => {
  if (!user) {
    console.warn('Cannot create log entry: user is null');
    return;
  }

  try {
    const { browser, device, deviceManufacturer, deviceModel } = getDeviceInfo();
    let ipAddress = 'unknown';
    try {
      ipAddress = await getIPAddress();
    } catch (e) {
      console.warn('Could not fetch IP address:', e);
    }

    const logEntry: ActivityLog = {
      id: crypto.randomUUID(),
      userId: user.id,
      username: user.username,
      role: user.role,
      action,
      module: action,
      details,
      ipAddress,
      deviceName: device,
      browser,
      deviceManufacturer,
      deviceModel,
      timestamp: getNowUTC(),
    };

    await addLog(logEntry);
  } catch (err) {
    console.error('Error in createLogEntry:', err);
  }
};

// ============================================
// EDIT REQUESTS
// ============================================

export const getEditRequests = async (): Promise<EditRequest[]> => {
  const { data, error } = await supabase
    .from('edit_requests')
    .select('*')
    .eq('event_id', eventId())
    .order('requested_at', { ascending: false });

  if (error) {
    console.error('Error fetching edit requests:', JSON.stringify(error));
    return [];
  }

  return (data || []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    patientId: r.patient_id as string,
    patientName: r.patient_name as string,
    vitalId: r.vital_id as string,
    vitalType: r.vital_type as string,
    currentValue: r.current_value as string,
    newValue: r.new_value as string,
    requestedBy: r.requested_by as string,
    requestedAt: r.requested_at as string,
    reason: r.reason as string,
    status: r.status as 'pending' | 'approved' | 'rejected',
    reviewedBy: r.reviewed_by as string | undefined,
    reviewedAt: r.reviewed_at as string | undefined,
  }));
};

export const addEditRequest = async (request: EditRequest): Promise<void> => {
  const { error } = await supabase
    .from('edit_requests')
    .insert([{
      id: request.id,
      event_id: eventId(),
      patient_id: request.patientId,
      patient_name: request.patientName,
      vital_id: request.vitalId,
      vital_type: request.vitalType,
      current_value: request.currentValue,
      new_value: request.newValue,
      requested_by: request.requestedBy,
      requested_at: request.requestedAt,
      reason: request.reason,
      status: request.status,
      reviewed_by: request.reviewedBy ?? null,
      reviewed_at: request.reviewedAt ?? null,
    }]);

  if (error) {
    console.error('Error adding edit request:', error);
    throw error;
  }
};

export const updateEditRequestStatus = async (
  id: string,
  status: 'approved' | 'rejected',
  reviewedBy: string
): Promise<void> => {
  const { error } = await supabase
    .from('edit_requests')
    .update({
      status,
      reviewed_by: reviewedBy,
      reviewed_at: getNowUTC(),
    })
    .eq('id', id)
    .eq('event_id', eventId());

  if (error) {
    console.error('Error updating edit request:', error);
    throw error;
  }

  if (status === 'approved') {
    const { data: request } = await supabase
      .from('edit_requests')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (request && request.new_value !== undefined) {
      await updateVital(request.vital_id, { value: request.new_value });
    }
  }
};

// ============================================
// PATIENT EDIT REQUESTS
// ============================================

export const getPatientEditRequests = async (): Promise<PatientEditRequest[]> => {
  const { data, error } = await supabase
    .from('patient_edit_requests')
    .select('*')
    .eq('event_id', eventId())
    .order('requested_at', { ascending: false });

  if (error) {
    console.error('Error fetching patient edit requests:', error);
    return [];
  }

  return (data || []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    patientId: r.patient_id as string,
    requestType: r.request_type as 'edit' | 'delete',
    requestedBy: r.requested_by as string,
    requestedAt: r.requested_at as string,
    reason: r.reason as string,
    status: r.status as 'pending' | 'approved' | 'rejected',
    reviewedBy: r.reviewed_by as string | undefined,
    reviewedAt: r.reviewed_at as string | undefined,
    patientData: r.patient_data as Partial<Patient> | undefined,
  }));
};

export const addPatientEditRequest = async (request: PatientEditRequest): Promise<void> => {
  const { error } = await supabase
    .from('patient_edit_requests')
    .insert([{
      id: request.id,
      event_id: eventId(),
      patient_id: request.patientId,
      request_type: request.requestType,
      requested_by: request.requestedBy,
      requested_at: request.requestedAt,
      reason: request.reason,
      status: request.status,
      reviewed_by: request.reviewedBy ?? null,
      reviewed_at: request.reviewedAt ?? null,
      patient_data: request.patientData ?? null,
    }]);

  if (error) {
    console.error('Error adding patient edit request:', error);
    throw error;
  }
};

export const updatePatientEditRequestStatus = async (
  id: string,
  status: 'approved' | 'rejected',
  reviewedBy: string
): Promise<void> => {
  const { error } = await supabase
    .from('patient_edit_requests')
    .update({
      status,
      reviewed_by: reviewedBy,
      reviewed_at: getNowUTC(),
    })
    .eq('id', id)
    .eq('event_id', eventId());

  if (error) {
    console.error('Error updating patient edit request:', error);
    throw error;
  }

  if (status === 'approved') {
    const { data: request } = await supabase
      .from('patient_edit_requests')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (request) {
      if (request.request_type === 'delete') {
        await deletePatient(request.patient_id);
      } else if (request.request_type === 'edit' && request.patient_data) {
        await updatePatient(request.patient_id, request.patient_data);
      }
    }
  }
};

// ============================================
// WHATSAPP SHARE REQUESTS
// ============================================

export const getWhatsAppShareRequests = async (): Promise<WhatsAppShareRequest[]> => {
  const { data, error } = await supabase
    .from('whatsapp_share_requests')
    .select('*')
    .eq('event_id', eventId())
    .order('requested_at', { ascending: false });

  if (error) {
    console.error('Error fetching WhatsApp share requests:', error);
    return [];
  }

  return (data || []).map((r: Record<string, unknown>) => ({
    id: r.id as string,
    patientId: r.patient_id as string,
    patientName: r.patient_name as string,
    requestedBy: r.requested_by as string,
    requestedByName: r.requested_by_name as string,
    requestedAt: r.requested_at as string,
    status: r.status as 'pending' | 'approved' | 'rejected',
    reviewedBy: r.reviewed_by as string | undefined,
    reviewedAt: r.reviewed_at as string | undefined,
  }));
};

export const addWhatsAppShareRequest = async (request: WhatsAppShareRequest): Promise<void> => {
  const { error } = await supabase
    .from('whatsapp_share_requests')
    .insert([{
      id: request.id,
      event_id: eventId(),
      patient_id: request.patientId,
      patient_name: request.patientName,
      requested_by: request.requestedBy,
      requested_by_name: request.requestedByName,
      requested_at: request.requestedAt,
      status: request.status,
      reviewed_by: request.reviewedBy ?? null,
      reviewed_at: request.reviewedAt ?? null,
    }]);

  if (error) {
    console.error('Error adding WhatsApp share request:', error);
    throw error;
  }
};

export const updateWhatsAppShareRequestStatus = async (
  id: string,
  status: 'approved' | 'rejected',
  reviewedBy: string
): Promise<void> => {
  const { error } = await supabase
    .from('whatsapp_share_requests')
    .update({
      status,
      reviewed_by: reviewedBy,
      reviewed_at: getNowUTC(),
    })
    .eq('id', id)
    .eq('event_id', eventId());

  if (error) {
    console.error('Error updating WhatsApp share request:', error);
    throw error;
  }

  if (status === 'approved') {
    const { data: request } = await supabase
      .from('whatsapp_share_requests')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (request) {
      await markPatientWhatsAppShared(request.patient_id);
    }
  }
};

export const markPatientWhatsAppShared = async (patientId: string): Promise<void> => {
  await updatePatient(patientId, { whatsappShared: true });
};

export const resetPatientWhatsAppShared = async (patientId: string): Promise<void> => {
  await updatePatient(patientId, { whatsappShared: false });
};

// ============================================
// BANKS
// ============================================

export const getBanks = async (): Promise<Bank[]> => {
  const { data, error } = await supabase
    .from('banks')
    .select('*')
    .eq('event_id', eventId())
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching banks:', error);
    return [];
  }

  return (data || []).map((b: Record<string, unknown>) => ({
    id: b.id as string,
    type: b.type as Bank['type'],
    accountHolderName: b.account_holder_name as string,
    upiId: b.upi_id as string | undefined,
    bankName: b.bank_name as string | undefined,
    accountNumber: b.account_number as string | undefined,
    ifscCode: b.ifsc_code as string | undefined,
    branch: b.branch as string | undefined,
    balance: Number(b.balance),
    qrImageUrl: b.qr_image_url as string | undefined,
    createdAt: b.created_at as string,
    createdBy: b.created_by as string,
  }));
};

export const addBank = async (bank: Bank): Promise<void> => {
  const { error } = await supabase
    .from('banks')
    .insert([{
      id: bank.id,
      event_id: eventId(),
      type: bank.type,
      account_holder_name: bank.accountHolderName,
      upi_id: bank.upiId ?? null,
      bank_name: bank.bankName ?? null,
      account_number: bank.accountNumber ?? null,
      ifsc_code: bank.ifscCode ?? null,
      branch: bank.branch ?? null,
      balance: bank.balance,
      qr_image_url: bank.qrImageUrl ?? null,
      created_at: bank.createdAt,
      created_by: bank.createdBy,
    }]);

  if (error) {
    console.error('Error adding bank:', error);
    throw error;
  }
};

export const updateBank = async (id: string, updates: Partial<Bank>): Promise<void> => {
  const dbUpdates: Record<string, unknown> = {};
  if (updates.type !== undefined) dbUpdates.type = updates.type;
  if (updates.accountHolderName !== undefined) dbUpdates.account_holder_name = updates.accountHolderName;
  if (updates.upiId !== undefined) dbUpdates.upi_id = updates.upiId;
  if (updates.bankName !== undefined) dbUpdates.bank_name = updates.bankName;
  if (updates.accountNumber !== undefined) dbUpdates.account_number = updates.accountNumber;
  if (updates.ifscCode !== undefined) dbUpdates.ifsc_code = updates.ifscCode;
  if (updates.branch !== undefined) dbUpdates.branch = updates.branch;
  if (updates.balance !== undefined) dbUpdates.balance = updates.balance;
  if (updates.qrImageUrl !== undefined) dbUpdates.qr_image_url = updates.qrImageUrl;

  const { error } = await supabase
    .from('banks')
    .update(dbUpdates)
    .eq('id', id)
    .eq('event_id', eventId());

  if (error) {
    console.error('Error updating bank:', error);
    throw error;
  }
};

export const deleteBank = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('banks')
    .delete()
    .eq('id', id)
    .eq('event_id', eventId());

  if (error) {
    console.error('Error deleting bank:', error);
    throw error;
  }
};

// ============================================
// FEE COLLECTIONS
// ============================================

export const getFeeCollections = async (): Promise<FeeCollection[]> => {
  const { data, error } = await supabase
    .from('fee_collections')
    .select('*')
    .eq('event_id', eventId())
    .order('collected_at', { ascending: false });

  if (error) {
    console.error('Error fetching fee collections:', JSON.stringify(error));
    return [];
  }

  return (data || []).map((f: Record<string, unknown>) => ({
    id: f.id as string,
    patientId: f.patient_id as string,
    patientName: f.patient_name as string,
    amount: Number(f.amount),
    paymentMethod: f.payment_method as FeeCollection['paymentMethod'],
    bankId: f.bank_id as string | undefined,
    bankName: f.bank_name as string | undefined,
    collectedBy: f.collected_by as string,
    collectedAt: f.collected_at as string,
  }));
};

export const addFeeCollection = async (collection: FeeCollection): Promise<void> => {
  try {
    const { error: feeError } = await supabase
      .from('fee_collections')
      .insert([{
        id: collection.id,
        event_id: eventId(),
        patient_id: collection.patientId,
        patient_name: collection.patientName,
        amount: collection.amount,
        payment_method: collection.paymentMethod,
        bank_id: collection.bankId ?? null,
        bank_name: collection.bankName ?? null,
        collected_by: collection.collectedBy,
        collected_at: collection.collectedAt,
      }]);

    if (feeError) throw feeError;

    await updatePatient(collection.patientId, { paymentStatus: 'paid' });

    if (collection.paymentMethod === 'cash') {
      const cashHoldings = await getCashHoldings();
      const userCash = cashHoldings.find(c => c.userId === collection.collectedBy);

      const { data: userData } = await supabase
        .from('app_users')
        .select('username')
        .eq('id', collection.collectedBy)
        .maybeSingle();

      const actualUsername = userData?.username || collection.collectedBy;

      if (userCash) {
        await updateCashHolding(userCash.userId, {
          amount: userCash.amount + collection.amount,
        });
      } else {
        await supabase.from('cash_holdings').insert([{
          event_id: eventId(),
          user_id: collection.collectedBy,
          username: actualUsername,
          amount: collection.amount,
        }]);
      }
    } else if (collection.paymentMethod === 'bank') {
      const banks = await getBanks();
      const bank = banks.find(b => b.id === collection.bankId);
      if (bank) {
        await updateBank(bank.id, {
          balance: bank.balance + collection.amount,
        });
      }
    }
  } catch (error) {
    console.error('Error in addFeeCollection transaction:', error);
    throw error;
  }
};

// ============================================
// EXPENSES
// ============================================

export const getExpenses = async (): Promise<Expense[]> => {
  const { data, error } = await supabase
    .from('expenses')
    .select('*')
    .eq('event_id', eventId())
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching expenses:', error);
    return [];
  }

  return (data || []).map((e: Record<string, unknown>) => ({
    id: e.id as string,
    description: e.description as string,
    quantity: Number(e.quantity),
    rate: Number(e.rate),
    totalAmount: Number(e.total_amount),
    createdBy: e.created_by as string,
    createdAt: e.created_at as string,
  }));
};

export const addExpense = async (expense: Expense): Promise<void> => {
  const { error } = await supabase
    .from('expenses')
    .insert([{
      id: expense.id,
      event_id: eventId(),
      description: expense.description,
      quantity: expense.quantity,
      rate: expense.rate,
      total_amount: expense.totalAmount,
      created_by: expense.createdBy,
      created_at: expense.createdAt,
    }]);

  if (error) {
    console.error('Error adding expense:', error);
    throw error;
  }
};

export const deleteExpense = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('expenses')
    .delete()
    .eq('id', id)
    .eq('event_id', eventId());

  if (error) {
    console.error('Error deleting expense:', error);
    throw error;
  }
};

// ============================================
// CASH HOLDINGS
// ============================================

export const getCashHoldings = async (): Promise<CashHolding[]> => {
  const MAX_RETRIES = 3;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    const { data, error } = await supabase
      .from('cash_holdings')
      .select('*')
      .eq('event_id', eventId());

    if (error) {
      const isNetErr = /Failed to fetch|NetworkError|fetch failed|Load failed/i.test(error.message || '');
      if (attempt < MAX_RETRIES && isNetErr) {
        await new Promise((res) => setTimeout(res, 700 * attempt));
        continue;
      }
      console.error('Error fetching cash holdings:', error);
      return [];
    }

    return (data || []).map((c: Record<string, unknown>) => ({
      userId: c.user_id as string,
      username: c.username as string,
      amount: Number(c.amount),
    }));
  }

  return [];
};

export const updateCashHolding = async (userId: string, updates: Partial<CashHolding>): Promise<void> => {
  const dbUpdates: Record<string, unknown> = {};
  if (updates.amount !== undefined) dbUpdates.amount = updates.amount;
  if (updates.username !== undefined) dbUpdates.username = updates.username;

  const { error } = await supabase
    .from('cash_holdings')
    .update(dbUpdates)
    .eq('user_id', userId)
    .eq('event_id', eventId());

  if (error) {
    console.error('Error updating cash holding:', error);
    throw error;
  }
};

export const getCashHoldingByUserId = async (userId: string): Promise<CashHolding | null> => {
  const { data, error } = await supabase
    .from('cash_holdings')
    .select('*')
    .eq('user_id', userId)
    .eq('event_id', eventId())
    .maybeSingle();

  if (error) {
    console.error('Error fetching cash holding:', error);
    return null;
  }

  if (!data) return null;

  return {
    userId: data.user_id as string,
    username: data.username as string,
    amount: Number(data.amount),
  };
};

// ============================================
// CASH TRANSFERS
// ============================================

export const getCashTransfers = async (): Promise<CashTransfer[]> => {
  const MAX_RETRIES = 3;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    const { data, error } = await supabase
      .from('cash_transfers')
      .select('*')
      .eq('event_id', eventId())
      .order('transferred_at', { ascending: false });

    if (error) {
      const isNetErr = /Failed to fetch|NetworkError|fetch failed|Load failed/i.test(error.message || '');
      if (attempt < MAX_RETRIES && isNetErr) {
        await new Promise((res) => setTimeout(res, 700 * attempt));
        continue;
      }
      console.error('Error fetching cash transfers:', error);
      return [];
    }

    return (data || []).map((t: Record<string, unknown>) => ({
      id: t.id as string,
      fromUserId: t.from_user_id as string,
      fromUsername: t.from_username as string,
      toUserId: t.to_user_id as string,
      toUsername: t.to_username as string,
      amount: Number(t.amount),
      remarks: t.remarks as string,
      transferredAt: t.transferred_at as string,
      transferredBy: t.transferred_by as string,
    }));
  }

  return [];
};

export const addCashTransfer = async (transfer: CashTransfer): Promise<void> => {
  try {
    const senderCash = await getCashHoldingByUserId(transfer.fromUserId);
    const receiverCash = await getCashHoldingByUserId(transfer.toUserId);

    if (!senderCash || senderCash.amount < transfer.amount) {
      throw new Error('Insufficient cash balance');
    }

    const { error: transferError } = await supabase
      .from('cash_transfers')
      .insert([{
        id: transfer.id,
        event_id: eventId(),
        from_user_id: transfer.fromUserId,
        from_username: transfer.fromUsername,
        to_user_id: transfer.toUserId,
        to_username: transfer.toUsername,
        amount: transfer.amount,
        remarks: transfer.remarks,
        transferred_at: transfer.transferredAt,
        transferred_by: transfer.transferredBy,
      }]);

    if (transferError) {
      console.error('Error inserting cash transfer:', transferError);
      throw transferError;
    }

    const { error: senderError } = await supabase
      .from('cash_holdings')
      .update({ amount: senderCash.amount - transfer.amount })
      .eq('user_id', transfer.fromUserId)
      .eq('event_id', eventId());

    if (senderError) {
      console.error('Error updating sender cash:', senderError);
      throw senderError;
    }

    if (receiverCash) {
      const { error: receiverError } = await supabase
        .from('cash_holdings')
        .update({ amount: receiverCash.amount + transfer.amount })
        .eq('user_id', transfer.toUserId)
        .eq('event_id', eventId());

      if (receiverError) {
        console.error('Error updating receiver cash:', receiverError);
        throw receiverError;
      }
    } else {
      const { error: createError } = await supabase
        .from('cash_holdings')
        .insert([{
          event_id: eventId(),
          user_id: transfer.toUserId,
          username: transfer.toUsername,
          amount: transfer.amount,
        }]);

      if (createError) {
        console.error('Error creating receiver cash holding:', createError);
        throw createError;
      }
    }

    console.log('Cash transfer completed successfully');
  } catch (error) {
    console.error('Error in addCashTransfer:', error);
    throw error;
  }
};

// ============================================
// BANK TRANSFERS
// ============================================

export const getBankTransfers = async (): Promise<BankTransfer[]> => {
  const { data, error } = await supabase
    .from('bank_transfers')
    .select('*')
    .eq('event_id', eventId())
    .order('transferred_at', { ascending: false });

  if (error) {
    console.error('Error fetching bank transfers:', error);
    return [];
  }

  return (data || []).map((t: Record<string, unknown>) => ({
    id: t.id as string,
    fromBankId: t.from_bank_id as string,
    fromBankName: t.from_bank_name as string,
    toBankId: t.to_bank_id as string,
    toBankName: t.to_bank_name as string,
    amount: Number(t.amount),
    remarks: t.remarks as string,
    transferredAt: t.transferred_at as string,
    transferredBy: t.transferred_by as string,
  }));
};

export const addBankTransfer = async (transfer: BankTransfer): Promise<void> => {
  try {
    const { error: transferError } = await supabase
      .from('bank_transfers')
      .insert([{
        id: transfer.id,
        event_id: eventId(),
        from_bank_id: transfer.fromBankId,
        from_bank_name: transfer.fromBankName,
        to_bank_id: transfer.toBankId,
        to_bank_name: transfer.toBankName,
        amount: transfer.amount,
        remarks: transfer.remarks,
        transferred_at: transfer.transferredAt,
        transferred_by: transfer.transferredBy,
      }]);

    if (transferError) throw transferError;

    const banks = await getBanks();
    const sourceBank = banks.find(b => b.id === transfer.fromBankId);
    if (sourceBank) {
      await updateBank(sourceBank.id, {
        balance: sourceBank.balance - transfer.amount,
      });
    }

    const destBank = banks.find(b => b.id === transfer.toBankId);
    if (destBank) {
      await updateBank(destBank.id, {
        balance: destBank.balance + transfer.amount,
      });
    }
  } catch (error) {
    console.error('Error in addBankTransfer transaction:', error);
    throw error;
  }
};